// Application root: login check, sync client start (opened section first), then the shell. Shown before the data arrives: the frame, never a «loading» banner.

import { useEffect, useState } from 'react';
import { SyncClient } from '../sync/client.ts';
import { SyncProvider } from '../sync/react.tsx';
import { ToastProvider, cx, ui, useToast } from '../ui/primitives.tsx';
import { AcceptInvite } from './AcceptInvite.tsx';
import { Login } from './Login.tsx';
import { FixDialog, fixable, type FixRequest } from '../record/FixDialog.tsx';
import { parseRoute } from './router.ts';
import { Shell } from './Shell.tsx';
import { apply } from './variants.tsx';
import { installScrollbars } from '../ui/scrollbars.ts';
import s from './shell.module.css';

apply();
installScrollbars();

type Phase = { t: 'checking' } | { t: 'login' } | { t: 'loading'; client: SyncClient } | { t: 'ready'; client: SyncClient } | { t: 'error'; message: string; action?: { label: string; href: string } };

export function AppRoot() {
  return <ToastProvider><Root /></ToastProvider>;
}

function Root() {
  const [phase, setPhase] = useState<Phase>({ t: 'checking' });
  const [fix, setFix] = useState<FixRequest | null>(null);
  const toast = useToast();

  const boot = async () => {
    // An entry from the personal page (R-A9): the one-time link becomes the external person's session, then the app starts as usual.
    const enter = /^\/enter\/([0-9a-f-]{36})$/.exec(location.pathname);
    if (enter) {
      const r = await fetch('/api/auth/enter', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ id: enter[1], token: new URLSearchParams(location.search).get('t') ?? '' }) }).catch(() => null);
      history.replaceState(null, '', '/');
      if (!r?.ok) return setPhase({ t: 'error', message: ((await r?.json().catch(() => null)) as { error?: { message?: string } } | null)?.error?.message ?? 'Сервер недоступен. Проверьте соединение.' });
    }
    // A new email is confirmed by its link (decision 030 п. 8); the app then starts as usual with a word about it.
    const email = /^\/email\/([0-9a-f-]{36})$/.exec(location.pathname);
    if (email) {
      const r = await fetch('/api/auth/email', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ id: email[1], token: new URLSearchParams(location.search).get('t') ?? '' }) }).catch(() => null);
      history.replaceState(null, '', '/');
      const body = (await r?.json().catch(() => null)) as { email?: string; error?: { message?: string } } | null;
      toast({ kind: r?.ok ? 'info' : 'error', text: r?.ok ? `Адрес для входа — ${body?.email ?? 'новый'}` : body?.error?.message ?? 'Не удалось подтвердить адрес' });
    }
    const me = await fetch('/api/auth/me').catch(() => null);
    if (!me) return setPhase({ t: 'error', message: 'Сервер недоступен. Проверьте соединение.' });
    if (me.status === 404) return setPhase({ t: 'error', message: 'Такого пространства нет. Проверьте адрес.' });
    // A closed workspace says why: suspended, or frozen with the way to unfreeze it (decision 031).
    if (me.status === 423) {
      const e = ((await me.json().catch(() => null)) as { error?: { message?: string; action?: { label: string; href: string } } } | null)?.error;
      return setPhase({ t: 'error', message: e?.message ?? 'Пространство приостановлено. Напишите в поддержку ConfDeck.', ...(e?.action ? { action: e.action } : {}) });
    }
    if (me.status === 401) return setPhase({ t: 'login' });
    const { user } = await me.json() as { user: { id: string } };
    const client = new SyncClient({ scope: location.host });
    // A refusal that names missing fields of the record or a reason asks for them (FixDialog) and sends the change again; anything else is a message.
    client.onReject = ({ error }) => toast({ kind: 'error', text: error.message });
    client.fixer = ({ tx, error }) => { const f = fixable(client, tx, error); return f ? new Promise((resolve) => setFix({ ...f, resolve })) : null; };
    client.onStorageError = () => toast({ kind: 'error', text: 'Браузер не сохраняет правки: не закрывайте вкладку, пока не появится «Всё сохранено».' });
    (window as unknown as { __confdeck: SyncClient }).__confdeck = client;
    // The speed measurement (tools/bench) drives the engine directly; the handle is harmless on the same origin.
    void Promise.all([import('../sync/query.ts'), import('../sync/mutations.ts')]).then(([q, m]) => { (window as unknown as Record<string, unknown>).__confdeckTools = { LiveQuery: q.LiveQuery, ...m }; });
    setPhase({ t: 'loading', client });
    const route = parseRoute(location.pathname, new URLSearchParams(location.search));
    const focus = route.name === 'type' ? route.typeKey : null;
    try {
      await client.start(focus, user.id, () => setPhase({ t: 'ready', client }), route.name === 'type' ? route.viewId : null);
      setPhase({ t: 'ready', client });
    } catch (e) {
      setPhase({ t: 'error', message: (e as Error).message });
    }
  };

  useEffect(() => { void boot(); }, []);

  const logout = async () => {
    if (phase.t === 'ready' || phase.t === 'loading') {
      // Unsent edits are not dropped silently (audit 28.10, P2-9): the person decides.
      const n = phase.client.unsent;
      if (n && !confirm(`Не отправлено правок: ${n} — нет связи с сервером. Выйти и потерять их?`)) return;
      await phase.client.resetCache(); phase.client.close();
    }
    await fetch('/api/auth/logout', { method: 'POST' });
    // A fresh page: nothing of the previous person (live queries, module state, socket) survives into the next login.
    location.reload();
  };

  // An invitation link opens its own page whether or not someone is logged in on this browser.
  const invite = /^\/(invite|reset)\/([0-9a-f-]{36})$/.exec(location.pathname);
  if (invite && phase.t !== 'ready' && phase.t !== 'loading') { const t = new URLSearchParams(location.search).get('t') ?? ''; return <AcceptInvite userId={invite[2]!} token={t} reset={invite[1] === 'reset'} onDone={() => void boot()} />; }
  if (phase.t === 'login') return <Login onDone={() => void boot()} />;
  if (phase.t === 'error') return <div className={s.fatal}><div className={s.logo}>CD</div><p>{phase.message}</p>{phase.action ? <a className={cx(ui.btn, ui.primary)} href={phase.action.href}>{phase.action.label}</a> : <button type="button" onClick={() => location.reload()}>Повторить</button>}</div>;
  if (phase.t === 'ready') return <SyncProvider client={phase.client}><Shell onLogout={() => void logout()} />{fix && <FixDialog req={fix} onClose={() => setFix(null)} />}</SyncProvider>;
  return <Skeleton />;
}

/** The empty frame of the app while the first section arrives. */
function Skeleton() {
  return (
    <div className={s.app}>
      <nav className={s.rail}><div className={s.railTop} /></nav>
      <aside className={s.side}><div className={s.skelLine} style={{ width: 120 }} /><div className={s.skelLine} /><div className={s.skelLine} style={{ width: 140 }} /></aside>
      <main className={s.main}><header className={s.top} /><div className={s.content}>{Array.from({ length: 12 }, (_, i) => <div key={i} className={s.skelRow} style={{ animationDelay: `${i * 40}ms` }} />)}</div></main>
    </div>
  );
}
